import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import { PrinterType, Status } from '../../../../shared/enums/enum';
import { SharedLabelFormatResponse } from '../../../../shared/models/response-models/label-format-response.model';
import { NamedResponse } from '../../../../shared/models/response-models/named-response.model';
import { WeekColorResponseModel } from '../../../../shared/models/response-models/week-color-response.model';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { LabelFormatApiService } from '../../../../shared/services/label-format-api.service';
import { MasterService } from '../../../../shared/services/master.service';
import { QzTrayService } from '../../../../shared/services/qz-tray.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { SharedModule } from '../../../../shared/shared.module';
import { WorkService } from '../../../work-management/services/work-service';
import {
  LabelFormatViewModel,
  LabelViewModel,
} from '../../models/view-models/print-labels-view.model';
import { LabelaryService } from '../../services/labelary.service';

@Component({
  selector: 'app-print-label',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './print-label.component.html',
  styleUrl: './print-label.component.css',
})
export class PrintLabelComponent {
  @ViewChild('previewBtn') previewBtn!: ElementRef;

  userInfo: any;
  companyId: number = 0;
  isPreviewEnabled = false;
  barcode = '';
  stores: NamedResponse[] = [];
  printLabelModel: LabelViewModel = new LabelViewModel();
  departments: NamedResponse[] = [];
  productCategories: NamedResponse[] = [];
  productSubCategories: NamedResponse[] = [];
  labelFormats: SharedLabelFormatResponse[] = [];
  selectedPrinter: string = '';
  brands: NamedResponse[] = [];
  productionCycles: WeekColorResponseModel[] = [];

  previewZplCode: any;
  zplList: string[] = [];
  activeTag: number = 0;
  previewTagSize: any;
  labelImg: string = '';
  label: LabelFormatViewModel = new LabelFormatViewModel();

  constructor(
    private workService: WorkService,
    private masterService: MasterService,
    private labelFormatApiService: LabelFormatApiService,
    private labelaryService: LabelaryService,
    private sharedService: SharedService,
    private qzService: QzTrayService,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.userInfo = this.masterService.getUserDetailFromStorage();
    this.companyId = Number(this.userInfo.companyId);

    this.getProductionCycles();
    this.getStores();
    this.getProductDepartments();
    this.getBrands();
    this.getLabelFormats();
  }

  getProductionCycles() {
    this.workService.getPastProductionCycles(this.companyId).subscribe((res) => {
      this.productionCycles = res;
    });
  }

  getStores() {
    this.sharedService.getStores(this.companyId).subscribe((res) => {
      this.stores = res as NamedResponse[];
    });
  }

  getProductDepartments() {
    const queryParams: Record<string, any> = {
      CompanyId: this.companyId,
    };
    this.sharedService.getProductDepartmentsLite(queryParams).subscribe((res) => {
      this.departments = res;
    });
  }

  getBrands() {
    const queryParams: Record<string, any> = {
      CompanyId: this.companyId,
    };
    this.sharedService.getBrandsLite(queryParams).subscribe((res) => {
      this.brands = res;
    });
  }

  getProductCategories() {
    const queryParams: Record<string, any> = {
      CompanyId: this.companyId,
      ProductDepartmentId: this.printLabelModel.departmentId,
    };
    this.sharedService.getProductCategoriesLite(queryParams).subscribe((res) => {
      this.productCategories = res;
    });
  }

  getProductSubCategories() {
    const queryParams: Record<string, any> = {
      CompanyId: this.companyId,
      ProductDepartmentId: this.printLabelModel.departmentId,
      ParentProductCategoryId: this.printLabelModel.categoryId,
    };
    this.sharedService.getProductCategoriesLite(queryParams).subscribe((res) => {
      this.productSubCategories = res;
    });
  }

  getLabelFormats() {
    const queryParams: Record<string, any> = {
      CompanyId: this.companyId,
      Status: Status.Active,
    };
    this.labelFormatApiService.getLabelFormats(queryParams).subscribe((res) => {
      this.labelFormats = res;
    });
  }

  onStoreChange() {
    const s = this.stores.find((x) => x.id == this.printLabelModel.storeId);
    this.printLabelModel.storeName = s?.name || null;
  }

  onBrandChange() {
    const b = this.brands.find((x) => x.id == this.printLabelModel.brandId);
    this.printLabelModel.brandName = b?.name || null;
  }

  onDepartmentChange() {
    const d = this.departments.find((x) => x.id == this.printLabelModel.departmentId);
    this.printLabelModel.departmentName = d?.name || null;

    this.printLabelModel.categoryId = null;
    this.printLabelModel.categoryName = null;

    if (this.printLabelModel.departmentId) {
      this.getProductCategories();
    } else {
      this.productCategories = [];
    }
  }

  onCategoryChange() {
    const c = this.productCategories.find((x) => x.id == this.printLabelModel.categoryId);
    this.printLabelModel.categoryName = c?.name || null;
    this.getProductSubCategories();
  }

  onSubCategoryChange() {
    const c = this.productSubCategories.find((x) => x.id == this.printLabelModel.subCategoryId);
    this.printLabelModel.subCategoryName = c?.name || null;
  }

  onPrintFormatChange() {
    if (this.printLabelModel.printFormat == null) return;

    const enumVal =
      this.labelFormats.find((x) => x.id == this.printLabelModel.printFormat)?.printerType ??
      PrinterType.Unknown;

    this.selectedPrinter = PrinterType[enumVal];
  }

  generateBarcode(event: any) {
    this.barcode = event.target.value;
  }

  async startPrinting() {
    if (!this.zplList || this.zplList.length === 0) return;

    const printerName = this.selectedPrinter || 'ZebraLabel';

    try {
      await this.qzService.printZplBatch(printerName, this.zplList).toPromise();
    } catch {
      try {
        for (const zpl of this.zplList) {
          await this.qzService.printZpl(printerName, zpl).toPromise();
        }
      } catch (error) {
        this.errorHandlerService.handlePrintError(error);
      }
    }
  }

  printLabel() {
    if (!this.isValidated()) return;
    this.showPreview();
  }
  onReset() {
    this.printLabelModel = new LabelViewModel();
    this.barcode = '';
    this.zplList = [];
    this.labelImg = '';
    this.isPreviewEnabled = false;
    this.activeTag = 0;
    this.previewTagSize = null;
  }
  async showPreview() {
    if (this.printLabelModel.printFormat == null) return;

    this.activeTag = this.printLabelModel.printFormat;

    this.zplList = [];

    const qty = Number(this.printLabelModel.quantityToPrint) || 1;

    for (let i = 1; i <= qty; i++) {
      const singleZpl = await this.generatePreviewZpl(i, qty);
      this.zplList.push(singleZpl);
    }

    this.generatePreview(this.zplList[0]);

    if (this.isPreviewEnabled) {
      this.previewBtn.nativeElement.click();
    } else {
      this.startPrinting();
    }
  }

  generatePreview(zplCode: string) {
    this.labelImg = this.labelaryService.convertZplToPng1(zplCode, this.previewTagSize);
  }

  generatePreviewZpl(index: number, quantity: number) {
    const weekColor = this.printLabelModel.color;

    const map: Record<string, string> = {
      CompanyName: this.userInfo.company,
      Store: this.printLabelModel.storeName == null ? '' : this.printLabelModel.storeName,
      Department:
        this.printLabelModel.departmentName == null ? '' : this.printLabelModel.departmentName,
      Category: this.printLabelModel.categoryName == null ? '' : this.printLabelModel.categoryName,
      SubCategory:
        this.printLabelModel.subCategoryName == null ? '' : this.printLabelModel.subCategoryName,
      Brand: this.printLabelModel.brandName == null ? '' : this.printLabelModel.brandName,
      Item: this.printLabelModel.name == null ? '' : this.printLabelModel.name,
      SKU: this.printLabelModel.sku == null ? '' : this.printLabelModel.sku,
      BarcodeValue: this.printLabelModel.sku == null ? '' : this.printLabelModel.sku,
      Price: String(this.printLabelModel.price == null ? '' : this.printLabelModel.price),
      SizeSize: this.printLabelModel.sizeSize == null ? '' : this.printLabelModel.sizeSize,
      SizeText:
        this.printLabelModel.sizeSize.trim().length == 0 ||
        this.printLabelModel.sizeSize.trim() == null
          ? ''
          : 'Size',
      Color: this.printLabelModel.color == null ? '' : this.printLabelModel.color,
      WeekColor: weekColor,
      Date: moment().format('DD/MM').replace(/\//g, '//'),
      Piece: String(quantity),
      LabelIndex: String(index),
    };

    // Get selected ZPL
    this.labelFormats.forEach((res) => {
      if (res.id == this.activeTag) {
        this.previewZplCode = res.zplCode;
        this.previewTagSize = this.extractLabelSize(res.description ?? '');
      }
    });

    let replacedZplCode = this.previewZplCode;

    // Replace only FRD placeholders
    Object.keys(map).forEach((key) => {
      replacedZplCode = replacedZplCode.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), map[key] || '');
    });

    // Escape for URL
    replacedZplCode = replacedZplCode
      .replace(/#/g, '\\23')
      .replace(/\$/g, '\\24')
      .replace(/%/g, '\\25');

    return replacedZplCode;
  }

  extractLabelSize(input: string): string {
    const labelNumber = input.match(/[0-9]+(\.[0-9]+)?/)?.[0] || '';
    const sizeNumber = input.match(/[Xx]\s*([0-9]+(\.[0-9]+)?)"/)?.[1] || '';

    let result = `${labelNumber}x${sizeNumber}`;
    result = this.reverseString(result);
    return result;
  }

  reverseString(inputStr: string): string {
    const parts: string[] = inputStr.split('x');
    const reversedStr: string = parts.reverse().join('x');
    return reversedStr;
  }

  isValidated() {
    return (
      this.printLabelModel.printFormat != null &&
      this.printLabelModel.storeId != null &&
      this.printLabelModel.name != null &&
      this.printLabelModel.sku != null &&
      this.printLabelModel.price != null &&
      this.printLabelModel.quantityToPrint != null &&
      this.printLabelModel.quantityToPrint > 0
    );
  }
}
